// src/services/api.js
import axios from 'axios';
import { API_COLORS, API_HEADER, API_STATUS, API_ERRORS } from '../constants/apiConstants';
import { browserName } from 'react-device-detect';
export const fetchAppSettings = async (url, token) => {
    const defaultSettings = {
        loginButtonColor: API_COLORS.LOGIN_BUTTON,
        navBackgroundColor: API_COLORS.NAV_BACKGROUND_DEFAULT,
        navForegroundColor: API_COLORS.NAV_FOREGROUND,
        hideAppointmentFeature: 0,
        tZone: 'America/New_York',
    };

    try {
        const response = await axios.get(url, {
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                Accept: API_HEADER.ACCEPT_JSON
            }
        });

        if (response.status === API_STATUS.OK && response.data?.bannerBackground) {
            return {
                ...defaultSettings,
                navBackgroundColor: response.data?.headerTopColor,
                navForegroundColor: response.data?.headerForegroundColor,
                hideAppointmentFeature :response.data?.hideAppointmentFeature,
                tZone: response.data?.webtZone || 'America/New_York',
            };
        }
        return defaultSettings;
    } catch (error) {
        console.error('Error fetching app settings:', error);
        return defaultSettings;
    }
};

export const GetStreamChatToken = async (url, token) => {
    try {
        const response = await axios.get(url, {
            method: 'GET',
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                Accept: API_HEADER.ACCEPT_JSON,
            }
        });

        if (response.status !== API_STATUS.OK || !response.data.UserToken) {
            throw new Error(API_ERRORS.FETCH_TOKEN);
        }
        return { token: response.data.UserToken, expireat: response.data.ExpiresAt };
    } catch (error) {
        console.error('Error fetching token:', error);
        throw error;
    }
};

// Reads a Stream message through mobile-api, which uses Stream's server-side client: it still returns
// a message the user can no longer read (e.g. after Delete Conversation cleared their history).
// Resolves null when the API reports the message not found.
export const getStreamMessageFromApi = async (url, token) => {
    try {
        const response = await axios.get(url, {
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                Accept: API_HEADER.ACCEPT_JSON,
            }
        });
        return response.data?.message || null;
    } catch (error) {
        if (error.response?.status === API_STATUS.NOT_FOUND) return null;
        throw error;
    }
};

export const LoginToWebChatSystem = async ({ url, data, domain }) => {
    if (!data) throw new Error(API_ERRORS.INVALID_REQUEST);
    const formData = new URLSearchParams();
    formData.append('data', data);
    formData.append('domain', domain);
    try {
        const response = await axios.post(url, formData, {
            headers: {
                'Content-Type': API_HEADER.CONTENT_TYPE_FORM,
                Accept: API_HEADER.ACCEPT_JSON,
            },
            withCredentials: true,
        });

        const responseData = response.data;
        return {
            AccessToken: responseData.AccessToken,
            TokenType: responseData.TokenType || '',
            AccountID: responseData.AccountID,
            AccountIDTo: responseData.AccountIDTo,
            AccountKey: responseData.AccountKey,
            ClientID: responseData.ClientID,
            EventID: responseData.EventID,
            EventKey: responseData.EventKey,
            AccessLevel: responseData.AccessLevel,
            RegType: responseData.RegType || '',
            FirstName: responseData.FirstName,
            LastName: responseData.LastName,
            DaysUntil: responseData.DaysUntil,
            RefreshToken: responseData.RefreshToken,
            AccessTokenExpiresAt: responseData.AccessTokenExpiresAt,
            RefreshTokenExpiresAt: responseData.RefreshTokenExpiresAt,
        };
    } catch (error) {
        const errorMessage = error.response?.data || error.message || API_ERRORS.LOGIN_FAILED;
        console.error('Error logging in:', errorMessage);
        throw new Error(errorMessage);
    }
};

// Exchange a refresh token for a new access token (same contract as the
// mobile apps: POST /api/v2/auth/refresh with the identity fields that were
// returned at login).
export const RefreshWebChatToken = async ({ url, refreshToken, eventId, accountId, clientId }) => {
    try {
        const response = await axios.post(url, {
            refreshToken,
            eventId: String(eventId),
            accountId: String(accountId),
            clientId: String(clientId),
        }, {
            headers: {
                'Content-Type': API_HEADER.CONTENT_TYPE_JSON,
                Accept: API_HEADER.ACCEPT_JSON,
            },
        });

        if (response.status !== API_STATUS.OK || !response.data?.accessToken) {
            throw new Error(API_ERRORS.FETCH_TOKEN);
        }
        return {
            AccessToken: response.data.accessToken,
            RefreshToken: response.data.refreshToken,
            AccessTokenExpiresAt: response.data.accessTokenExpiresAt,
            ExpiresIn: response.data.expiresIn,
        };
    } catch (error) {
        console.error('Error refreshing access token:', error);
        throw error;
    }
};

// Function to save FCM token to the server
export const saveFCMToken = async (url, token, fcmToken) => {
    try {
        const formData = new URLSearchParams();
        formData.append('fcmToken', fcmToken);
        formData.append('browsername', browserName || 'Unknown');

        const response = await axios.post(url, formData, {
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                'Content-Type': API_HEADER.CONTENT_TYPE_FORM,
            },
        });

        if (response.status !== API_STATUS.OK) {
            throw new Error(API_ERRORS.SAVE_FAILED);
        }
        return response.data;
    } catch (error) {
        console.error("Error saving FCM token:", error);
        throw error;
    }
};

// Function to delete FCM token from the server 
export const deleteFCMToken = async (url, token, fcmToken) => {
    try {
        const formData = new URLSearchParams();
        formData.append('fcmToken', fcmToken);

        const response = await axios.post(url, formData, {
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                'Content-Type': API_HEADER.CONTENT_TYPE_FORM,
            },
        });

        if (response.status !== API_STATUS.NO_CONTENT
            && response.status !== API_STATUS.OK) {
            throw new Error(API_ERRORS.DELETE_FAILED);
        }
    } catch (error) {
        console.error("Error deleting FCM token:", error);
        throw error;
    }
};

// Function to check if FCM token exists on the server
export const checkFCMToken = async (url, fcmToken, token,browserName) => {
    try {
        const response = await axios.get(`${url}?fcmToken=${fcmToken}&browsername=${browserName}`, {
            headers: {
                Authorization: `${API_HEADER.AUTH_PREFIX} ${token}`,
                Accept: API_HEADER.ACCEPT_JSON,
            },
        });

        if (response.status !== API_STATUS.OK
            || response.data.length <= 0) {
            return null;
        }
        return (!!((response.data.length > 0 && response.data[0].isActive)));
    } catch (error) {
        console.error("Error checking FCM token:", error);
        throw error;
    }
};

